import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { useGetSupportPackagesInfiniteQuery, useGetUsersLookupQuery } from 'src/apiClients';
import type { SupportPackageItem } from 'src/apiClients/queries';
import {
    SortDirection,
    type DataTableCellValue,
    type DataTableColDef,
    type DataTableRow,
} from 'src/components/data-table/DataTable.types';
import { EntityListPage } from 'src/components/entity-list-page';

import { i18n } from './SupportPackagesPage.i18n';

const SEARCH_COLUMNS = ['message'];
// UUID columns can't use $iLike (Postgres type mismatch); they're matched
// exactly via $eq when the search term is a full UUID. This lets a user paste
// a full id/userId/subscriptionId into the search box and get an exact match.
const UUID_SEARCH_COLUMNS = ['id', 'userId', 'subscriptionId'];
const INITIAL_SORT = [{ field: 'createdAt', direction: SortDirection.DESC }];

const formatDateTime = (value: DataTableCellValue): string => {
    if (!value || typeof value !== 'string') return '';
    const parsed = dayjs(value);
    if (!parsed.isValid()) return String(value);
    return parsed.format('D MMM YYYY, hh:mm A');
};

const downloadSupportPackage = (row: DataTableRow) => {
    const url = row.url;
    if (typeof url !== 'string' || url === '') return;
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
    window.open(parsed.toString(), '_blank', 'noopener,noreferrer');
};

export const SupportPackagesPage = () => {
    const intl = useIntl();
    const location = useLocation();
    const highlightId = (location.state as { highlightId?: string } | null)?.highlightId;

    // Small lookup for username. Will be removed when backend embeds user on
    // supportPackage.
    const { data: users = [] } = useGetUsersLookupQuery();
    const usernameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const user of users) map.set(user.id, user.login || user.email);
        return map;
    }, [users]);

    const mapRow = useCallback(
        (pkg: SupportPackageItem): DataTableRow => ({
            id: pkg.id,
            createdAt: pkg.createdAt,
            username: usernameById.get(pkg.userId) ?? '',
            message: pkg.message,
            url: pkg.url,
        }),
        [usernameById],
    );

    const columns = useMemo<DataTableColDef[]>(() => {
        const downloadLabel = intl.formatMessage(i18n.download);
        return [
            {
                field: 'createdAt',
                headerName: intl.formatMessage(i18n.createdAt),
                width: 215,
                valueFormatter: formatDateTime,
            },
            {
                field: 'username',
                headerName: intl.formatMessage(i18n.username),
                width: 270,
                clientSort: true,
            },
            {
                field: 'message',
                headerName: intl.formatMessage(i18n.message),
                flex: 1,
                minWidth: 200,
                wrapText: true,
            },
            {
                field: 'url',
                headerName: downloadLabel,
                width: 160,
                sortable: false,
                copyable: false,
                tooltipText: intl.formatMessage(i18n.clickToDownload),
                valueFormatter: (value) => (value ? downloadLabel : ''),
                onCellClick: downloadSupportPackage,
            },
        ];
    }, [intl]);

    return (
        <EntityListPage<SupportPackageItem>
            automationId="support-packages-page"
            useQuery={useGetSupportPackagesInfiniteQuery}
            searchColumns={SEARCH_COLUMNS}
            uuidSearchColumns={UUID_SEARCH_COLUMNS}
            initialSort={INITIAL_SORT}
            highlightedRowId={highlightId}
            columns={columns}
            mapRow={mapRow}
            titleWhenNoResults={intl.formatMessage(i18n.noPackagesTitle)}
            messageWhenNoResults={intl.formatMessage(i18n.noPackagesMessage)}
        />
    );
};
